import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import { buildAuthRoute, buildPaymentRoute } from "utils/checkoutFlow";
import { APP_BASE_ROUTE, LANDING_ROUTE, SIGN_IN_ROUTE, SIGN_UP_ROUTE, isAppRoute } from "utils/routes";
import { isNativeApp } from "utils/platform";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";
import CookieConsentManager from "components/CookieConsentManager";

const PRO_TIER = 3;

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    direction,
    layout,
    miniSidenav,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    userTier
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const hasAuthToken =
    typeof window !== "undefined" && Boolean(window.localStorage.getItem("tradez-token"));
  const nativeApp = isNativeApp();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);

  }, []);


  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const manifestId = "app-manifest-link";
    const existingManifest = document.getElementById(manifestId);

    if (isAppRoute(pathname)) {
      if (!existingManifest) {
        const manifestLink = document.createElement("link");

        manifestLink.id = manifestId;
        manifestLink.rel = "manifest";
        manifestLink.href = `${process.env.PUBLIC_URL || ""}/manifest.json`;

        document.head.appendChild(manifestLink);
      }

      return;
    }

    existingManifest?.remove();
  }, [pathname]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const routeComponent =
          nativeApp && route.route === LANDING_ROUTE
            ? <Navigate to={SIGN_IN_ROUTE} replace />
            : route.component;
        const routeRequiresAuth = Boolean(route.requiresAuth);

        return <Route 
          exact path={route.route} 
          element={routeRequiresAuth && !hasAuthToken
              ? <Navigate to={SIGN_IN_ROUTE} replace />
              : Number(userTier ?? 1) < PRO_TIER && route.isPro
              ? <Navigate
                  to={
                    hasAuthToken
                      ? buildPaymentRoute("pro")
                      : buildAuthRoute(SIGN_UP_ROUTE, "pro", { upgradeNow: 1 })
                  }
                  replace
                />
              : routeComponent} 
          key={route.key} />;
      }

      return null;
    });

  const flattenRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return flattenRoutes(route.collapse);
      }

      return route.route ? [route] : [];
  });

  const currentRoute = flattenRoutes(routes).find((route) => route.route === pathname);
  const hideAppChrome = Boolean(
    currentRoute?.hideSideMenu || (currentRoute?.requiresAuth && !hasAuthToken)
  );
  const fallbackRoute = nativeApp ? (hasAuthToken ? APP_BASE_ROUTE : SIGN_IN_ROUTE) : LANDING_ROUTE;

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        <CookieConsentManager />
        {!hideAppChrome && layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Patrimônio360"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {/* {configsButton} */}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to={fallbackRoute} replace />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <CookieConsentManager />
      {!hideAppChrome && layout === "dashboard" &&  (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="Patrimônio360"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="*" element={<Navigate to={fallbackRoute} replace />} />
      </Routes>
    </ThemeProvider>
  );
}
